import React from 'react';
import { useField } from 'formik';
import { useSchemaContext } from './SparkForm';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import dayJs from 'dayjs';
import {
  FormHelperText,
  Grid,
  GridProps,
  InputLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { useResize } from 'hooks/common';
import type { InputProps, TextFieldProps } from '@mui/material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Dayjs } from 'dayjs';

type SparkFormTimePickerProps = {
  name: string;
  label: string | React.ReactNode;
  placeholder?: string;
  size?: GridProps['xs'];
  type?: TextFieldProps['type'];
  readonly?: boolean;
  sx?: TextFieldProps['sx'];
  startAdornment?: InputProps['startAdornment'];
  endAdornment?: InputProps['endAdornment'];
  helpText?: string;
  subHeaderText?: string;
  autoLowerCase?: boolean;
} & Omit<TextFieldProps, 'sx' | 'children' | 'size'>;

function SparkFormTimePicker({
  name,
  label,
  placeholder,
  size,
  type = 'text',
  readonly = false,
  sx,
  startAdornment = <></>,
  endAdornment = <></>,
  helpText = '',
  subHeaderText = '',
  autoLowerCase = false,
  ...rest
}: SparkFormTimePickerProps) {
  const [field, meta, helper] = useField<string>(name);
  const { validationSchema } = useSchemaContext();
  const { isMobile } = useResize();

  const isFieldRequired = React.useMemo(() => {
    try {
      const fieldSchema = validationSchema.describe().fields[name];
      // @ts-ignore
      const isRequiredSchema = fieldSchema.tests.find(
        //@ts-ignore
        (test) => test.name === 'requiredTest'
      );
      if (isRequiredSchema) {
        return true;
      }

      if ('optional' in fieldSchema) {
        return !fieldSchema.optional;
      }
      return false;
    } catch {
      return false;
    }
  }, [name, validationSchema]);
  const isError = meta.touched && !!meta.error;

  const handleChange = (newDate: Dayjs | null) => {
    if (!newDate) {
      return;
    }
    helper.setValue(newDate.toISOString());
  };

  const defaultSize = size || (isMobile ? 12 : 6);

  return (
    <Grid item xs={defaultSize} container direction='column'>
      <InputLabel
        sx={{ marginLeft: 1, display: 'flex', gap: 0.5, fontSize: 16 }}
      >
        <span>{isFieldRequired && '*'}</span>
        {label}
        {helpText && (
          <Tooltip title={helpText} placement='top' arrow enterTouchDelay={0}>
            <InfoOutlinedIcon fontSize='small' />
          </Tooltip>
        )}
      </InputLabel>
      {subHeaderText && (
        <Typography
          color='#A3A3A3'
          marginLeft={1.6}
          fontSize={12}
          textAlign='left'
        >
          {subHeaderText}
        </Typography>
      )}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <TimePicker onChange={handleChange} value={dayJs(field?.value)} />
      </LocalizationProvider>
      <FormHelperText
        sx={{
          color: (theme) => theme.palette.error.main,
          marginLeft: 1,
          marginBottom: 0.5,
        }}
      >
        {isError ? meta.error : ' '}
      </FormHelperText>
    </Grid>
  );
}

export default SparkFormTimePicker;
